import axios, { AxiosResponse } from 'axios';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AuthenticationResult,
  AccountInfo,
} from '@azure/msal-browser';

import Constants from './Constants';

let msalInstance: PublicClientApplication;
let loginRequest: { scopes: string[] };

export function initializeAuthService(
  instance: PublicClientApplication,
  request: { scopes: string[] }
) {
  msalInstance = instance;
  loginRequest = request;
}

export class AuthService {
  private static _instance: AuthService;

  private expirationIntervalId: NodeJS.Timeout | null = null;
  private expirationTimeInMinutes: number | null = null;

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  public async initialize() {
    if (this.expirationTimeInMinutes !== null) {
      return;
    }
    try {
      const baseUrl = window.location.origin + '/api/WHCPortal';
      const result: AxiosResponse<any> = await axios({
        method: 'get',
        url: `${baseUrl}/GetAuthSettings`,
        withCredentials: true,
      });
      const authSettings = result.data;
      this.expirationTimeInMinutes = authSettings.expirationTimeInMinutes;
    } catch (error) {
      console.error(
        'Failed to initialize AuthService auth settings from SharePoint.',
        error
      );
      console.warn('Defaulting to 30 minutes for session expiration.');
      this.expirationTimeInMinutes = 30; // Default to 30 minutes as a fallback
    }
  }

  private startExpirationTimer() {
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
    }
    this.expirationIntervalId = setInterval(() => {
      this.checkExpiration();
    }, 15000); // Check every 15 seconds
  }

  private stopExpirationTimer() {
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
      this.expirationIntervalId = null;
    }
  }

  public async setExpirationTime() {
    if (this.expirationTimeInMinutes === null) {
      return;
    }
    const now = new Date();
    const expirationTime =
      now.getTime() + this.expirationTimeInMinutes * 60 * 1000;
    localStorage.setItem(
      Constants.LOCAL_STORAGE_KEYS.EXPIRATION_TIME,
      expirationTime.toString()
    );
  }

  public checkExpiration() {
    const expirationTime = localStorage.getItem(
      Constants.LOCAL_STORAGE_KEYS.EXPIRATION_TIME
    );
    if (expirationTime) {
      const now = new Date().getTime();
      if (now > parseInt(expirationTime)) {
        this.removeExpirationTime();
        localStorage.setItem(
          Constants.LOCAL_STORAGE_KEYS.IS_SESSION_TIMED_OUT,
          'true'
        );
        const account = msalInstance.getActiveAccount();
        if (account) {
          msalInstance.logoutPopup({ account });
        }
      }
    }
  }

  public removeExpirationTime() {
    localStorage.removeItem(Constants.LOCAL_STORAGE_KEYS.EXPIRATION_TIME);
    this.stopExpirationTimer();
  }

  public loginMsal(): void {
    msalInstance.loginPopup(loginRequest);
  }

  public logoutMsal(): void {
    const account = msalInstance.getActiveAccount();
    if (account) {
      msalInstance.logoutPopup({ account });
    }
  }

  /**
   * MSAL: Acquires an access token silently, with a fallback to an interactive redirect.
   * This is the primary method to get a token for API calls protected by MSAL.
   * @returns {Promise<string | null>} A promise that resolves to the access token or null if acquisition fails.
   */
  public async getAccessTokenMsal(): Promise<string | null> {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    const request = {
      ...loginRequest,
      account,
    };

    try {
      const response: AuthenticationResult =
        await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn(
          'Silent token acquisition failed. Falling back to interactive redirect.'
        );
        msalInstance.acquireTokenRedirect(request);
      } else {
        console.error('MSAL Token acquisition failed:', error);
      }
      return null;
    }
  }

  /**
   * MSAL: Gets the currently signed-in user's account object from MSAL.
   * @returns {AccountInfo | null} The account object or null if not signed in.
   */
  public getCurrentUserMsal(): AccountInfo | null {
    return msalInstance.getActiveAccount();
  }

  public async getCurrentUser(): Promise<{
    name: string;
    userType: 'internal' | 'external';
  } | null> {
    let user: { name: string; userType: 'internal' | 'external' } | null = null;
    this.checkExpiration();

    try {
      // Check auth using entra external id auth
      const account = this.getCurrentUserMsal();
      if (account?.name) {
        user = { name: account.name, userType: 'external' };
      } else {
        // Check auth using internal auth (easy auth)
        const result: AxiosResponse<any> = await axios({
          method: 'get',
          url: `/.auth/me`,
          withCredentials: true,
        });

        const name = result.data?.[0]?.user_claims?.find(
          (claim: any) => claim.typ === 'name'
        )?.val;
        if (name) {
          user = { name, userType: 'internal' };
        }
      }

      if (!user) {
        this.removeExpirationTime();
        return null;
      }

      this.setExpirationTime();
      this.startExpirationTimer();
      this.clearTimeoutFlag();
      return user;
    } catch (error) {
      console.error('Failed to get current user name', error);
      this.removeExpirationTime();
      return null;
    }
  }

  public checkIsSessionTimedOut(): boolean {
    const isTimedOut = localStorage.getItem(
      Constants.LOCAL_STORAGE_KEYS.IS_SESSION_TIMED_OUT
    );
    return isTimedOut === 'true';
  }

  public clearTimeoutFlag() {
    localStorage.removeItem(Constants.LOCAL_STORAGE_KEYS.IS_SESSION_TIMED_OUT);
  }
}
